import {type RouteObject, Navigate} from 'react-router-dom';
import {RootLayout} from './RootLayout';
import {ProtectedLayout} from './ProtectedLayout';
import {AdminLayout} from './AdminLayout';
import {TrainingProgramLayout} from './TrainingProgramLayout';
import {ErrorBoundary} from './ErrorBoundary';
import {IndexRedirect} from './IndexRedirect';
import {QuotingRoute} from './QuotingRoute';
import {
  LegacyInboxRedirect,
  QuotingHomeRedirect,
  QuotingLibraryRedirect,
} from './QuotingHomeRedirect';

// Lazy load route components for code splitting
import {lazy, Suspense, type ReactNode} from 'react';
import {QuoteDeskLoading} from '@/matic/features/quoting/components/QuoteDeskLoading';
import AppBootOverlay from '@/matic/components/ui/AppBootOverlay';
import {HideAppLoader} from '@/matic/lib/utils/hide-app-loader';

// Auth routes (public). LoginPage is eager-imported: every unauthenticated
// visitor hits it, and splitting it off just adds a serialized chunk fetch on
// the cold path that already has to wait for two Cognito hostnames to resolve.
import LoginPage from './pages/LoginPage';

const LogoutPage = lazy(async () => import('./pages/LogoutPage'));
const QuoteIntakeRequestPage = lazy(
  async () =>
    import('@/matic/features/quoting/components/QuoteIntakeRequestPage')
);
const AuthErrorPage = lazy(async () => import('./pages/AuthErrorPage'));
const UnauthorizedPage = lazy(async () => import('./pages/UnauthorizedPage'));

// Protected routes
const DocumentsManagerPage = lazy(
  async () => import('./pages/documents/DocumentsManagerPage')
);
const DocumentTypeDetailPage = lazy(
  async () => import('./pages/documents/DocumentTypeDetailPage')
);
const DocumentDetailPage = lazy(
  async () => import('./pages/documents/DocumentDetailPage')
);
const DocumentNewPage = lazy(
  async () => import('./pages/documents/DocumentNewPage')
);
const DocumentTypeNewPage = lazy(
  async () => import('./pages/documents/DocumentTypeNewPage')
);

const TrainingManagerPage = lazy(
  async () => import('./pages/training/TrainingManagerPage')
);
const TrainingProgramDetailPage = lazy(
  async () => import('./pages/training/TrainingProgramDetailPage')
);
const TrainingProgramNewPage = lazy(
  async () => import('./pages/training/TrainingProgramNewPage')
);

const CurriculumNewPage = lazy(
  async () => import('./pages/training/curricula/CurriculumNewPage')
);
const CurriculumDetailPage = lazy(
  async () => import('./pages/training/curricula/CurriculumDetailPage')
);

const MyTrainingListPage = lazy(
  async () => import('./pages/my-training/MyTrainingListPage')
);
const MyTrainingAssignmentPage = lazy(
  async () => import('./pages/my-training/MyTrainingAssignmentPage')
);
const InstructorAttestationPage = lazy(
  async () => import('./pages/my-training/InstructorAttestationPage')
);
const InstructorSignoffPage = lazy(
  async () => import('./pages/my-training/InstructorSignoffPage')
);

const InboxPage = lazy(async () => import('./pages/InboxPage'));
const ProfilePage = lazy(async () => import('./pages/ProfilePage'));
const ScoutPage = lazy(async () => import('./pages/ScoutPage'));
const SharedChatPage = lazy(async () => import('./pages/chat/SharedChatPage'));
const LatticeListPage = lazy(
  async () => import('./pages/lattice/LatticeListPage')
);
const LatticeCreatePage = lazy(
  async () => import('./pages/lattice/LatticeCreatePage')
);
const LatticeDetailPage = lazy(
  async () => import('./pages/lattice/LatticeDetailPage')
);
const ProcurementPage = lazy(
  async () => import('./pages/procurement/ProcurementPage')
);
// One chunk for the three Quote Desk pages, so hopping between them never suspends
// (see quote-desk-pages.ts).
const quoteDeskPage = (
  name: 'QuotesPage' | 'QuoteManagementPage' | 'QuoteWorkspacePage'
) =>
  lazy(async () => {
    const pages = await import('./pages/quoting/quote-desk-pages');
    return {default: pages[name]};
  });
const QuotesPage = quoteDeskPage('QuotesPage');
const QuoteManagementPage = quoteDeskPage('QuoteManagementPage');
const QuoteWorkspacePage = quoteDeskPage('QuoteWorkspacePage');
// While a Quote Desk chunk loads, show the rider on white instead of the layout's
// empty fallback, so arriving at the desk never flashes blank.
const quoteDesk = (page: ReactNode) => (
  <Suspense fallback={<QuoteDeskLoading />}>{page}</Suspense>
);
const QuotePricingResourcePage = lazy(
  async () => import('./pages/quoting/QuotePricingResourcePage')
);
// The generic workspace (tenants whose engine's `workspace` is `generic`), one chunk.
const genericQuotePage = (
  name: 'GenericQuoteQueuePage' | 'GenericQuoteRequestPage'
) =>
  lazy(async () => {
    const pages = await import('./pages/quoting/generic-quote-pages');
    return {default: pages[name]};
  });
const GenericQuoteQueuePage = genericQuotePage('GenericQuoteQueuePage');
const GenericQuoteRequestPage = genericQuotePage('GenericQuoteRequestPage');

// Records routes
const RecordTypesListPage = lazy(
  async () => import('./pages/records/RecordTypesListPage')
);
const RecordTypeNewPage = lazy(
  async () => import('./pages/records/RecordTypeNewPage')
);
const RecordTypeDetailPage = lazy(
  async () => import('./pages/records/RecordTypeDetailPage')
);
const RecordTypeEditorPage = lazy(
  async () => import('./pages/records/RecordTypeEditorPage')
);
const RecordNewPage = lazy(async () => import('./pages/records/RecordNewPage'));
const RecordDetailPage = lazy(
  async () => import('./pages/records/RecordDetailPage')
);

// Scheduling routes
const CalendarPage = lazy(
  async () => import('./pages/scheduling/CalendarPage')
);
const WhatsDuePage = lazy(
  async () => import('./pages/scheduling/WhatsDuePage')
);
const SchedulingPage = lazy(
  async () => import('./pages/scheduling/SchedulingPage')
);
const ScheduleRuleNewPage = lazy(
  async () => import('./pages/scheduling/ScheduleRuleNewPage')
);
const ScheduleRuleDetailPage = lazy(
  async () => import('./pages/scheduling/ScheduleRuleDetailPage')
);

// Suppliers routes (Supplier Quality hub)
const SupplierListPage = lazy(
  async () => import('./pages/suppliers/SupplierListPage')
);
const SupplierRecordTypesPage = lazy(
  async () => import('./pages/suppliers/SupplierRecordTypesPage')
);
const SupplierDetailPage = lazy(
  async () => import('./pages/suppliers/SupplierDetailPage')
);

const InboxSourcePage = lazy(
  async () => import('./pages/triage/InboxSourcePage')
);
const TriagePage = lazy(async () => import('./pages/triage/TriagePage'));
const LibraryPage = lazy(async () => import('./pages/library/LibraryPage'));
const HomePage = lazy(async () => import('./pages/home/HomePage'));

// Admin routes
const AdminUsersPage = lazy(async () => import('./pages/admin/AdminUsersPage'));
const GroupsManagementPage = lazy(
  async () => import('./pages/admin/GroupsManagementPage')
);
const GroupDetailPage = lazy(
  async () => import('./pages/admin/GroupDetailPage')
);
const AdminMasterDataPage = lazy(
  async () => import('./pages/admin/AdminMasterDataPage')
);
const AdminRelayConfigPage = lazy(
  async () => import('./pages/admin/AdminRelayConfigPage')
);
const ObjectTypeDetailPage = lazy(
  async () => import('./pages/admin/ObjectTypeDetailPage')
);
const ObjectRecordDetailPage = lazy(
  async () => import('./pages/admin/ObjectRecordDetailPage')
);

// Super Admin routes (internal, no Cognito auth required)
const SuperAdminTenantsPage = lazy(
  async () => import('./pages/super-admin/SuperAdminTenantsPage')
);

// Error pages
const NotFoundPage = lazy(async () => import('./pages/NotFoundPage'));

/**
 * React Router v6 route configuration
 *
 * Structure:
 * - Root layout (providers, global UI)
 *   - Public routes (login, logout, auth/error, unauthorized)
 *   - Protected layout (AuthGuard + Sidebar)
 *     - Protected routes (documents, training, profile, inbox, etc.)
 *     - Admin layout (checks admin.access_section permission)
 *       - Admin routes (admin/users, admin/document-config)
 *
 * Authorization Layers:
 * 1. Authentication: ProtectedLayout (AuthGuard) - checks if user is logged in
 * 2. Role-Based: AdminLayout - checks if user has admin.access_section permission
 * 3. Resource-Level: Individual pages (e.g., DocumentNewPage checks document.create)
 *
 * Benefits over Next.js hybrid approach:
 * - Single routing system (no Next.js pages + ClientRouter split)
 * - No hydration issues (pure client-side rendering)
 * - Consistent component trees (no remounting on navigation)
 * - Simpler mental model
 */
export const routeConfig: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      // Public auth routes (wrapped in Suspense for lazy loading).
      // The Suspense fallback is `<AppBootOverlay />` so the visual
      // is continuous with index.html's #app-loader.
      //
      // `/login` and `/logout` are transient — they kick off a navigation that
      // takes the browser away to Cognito. They deliberately do NOT include
      // `<HideAppLoader />`: the persistent #app-loader must keep covering the
      // screen until the browser actually leaves, otherwise the M tracer gets
      // clipped mid-stroke. (The content routes below keep `<HideAppLoader />`
      // so the overlay fades once the real page mounts.)
      {
        path: 'login',
        element: (
          <Suspense fallback={<AppBootOverlay />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        path: 'logout',
        element: (
          <Suspense fallback={<AppBootOverlay />}>
            <LogoutPage />
          </Suspense>
        ),
      },
      {
        path: 'auth/error',
        element: (
          <Suspense fallback={<AppBootOverlay />}>
            <HideAppLoader />
            <AuthErrorPage />
          </Suspense>
        ),
      },
      {
        path: 'unauthorized',
        element: (
          <Suspense fallback={<AppBootOverlay />}>
            <HideAppLoader />
            <UnauthorizedPage />
          </Suspense>
        ),
      },

      // Protected routes (wrapped in AuthGuard + Sidebar)
      {
        element: <ProtectedLayout />,
        children: [
          // Index — on a quotes-first build this is the Quote Desk home; otherwise
          // it waits for OIDC to settle and redirects to /home. AuthGuard bounces
          // unauthenticated visitors to /login. See IndexRedirect.tsx for why it
          // waits (Cognito's redirect_uri is `/`, so a synchronous Navigate would
          // strip the auth code before react-oidc-context can process it).
          {
            index: true,
            element: <IndexRedirect home={quoteDesk(<QuotesPage />)} />,
          },

          // Documents
          {
            path: 'documents',
            element: <DocumentsManagerPage />,
          },
          {
            path: 'documents/types/new',
            element: <DocumentTypeNewPage />,
          },
          {
            path: 'documents/types/:id',
            element: <DocumentTypeDetailPage />,
          },
          {
            path: 'documents/new',
            element: <DocumentNewPage />,
          },
          {
            path: 'documents/:id',
            element: <DocumentDetailPage />,
          },

          // Training Manager - Admin/Quality only.
          // Programs + Curricula share a single tabbed page; the
          // ``/training/programs`` and ``/training/curricula`` paths
          // route to the same component with the right ``?tab=`` so
          // existing bookmarks keep working.
          {
            element: <TrainingProgramLayout />,
            children: [
              {
                path: 'training/manager',
                element: <TrainingManagerPage />,
              },
              {
                path: 'training/programs',
                element: <TrainingManagerPage />,
              },
              {
                path: 'training/programs/new',
                element: <TrainingProgramNewPage />,
              },
              {
                path: 'training/programs/:id',
                element: <TrainingProgramDetailPage />,
              },
              {
                path: 'training/curricula',
                element: (
                  <Navigate to="/training/manager?tab=curricula" replace />
                ),
              },
              {
                path: 'training/curricula/new',
                element: <CurriculumNewPage />,
              },
              {
                path: 'training/curricula/:id',
                element: <CurriculumDetailPage />,
              },
              {
                // Legacy bookmarks land on the Manager's Matrix tab.
                path: 'training/competency-matrix',
                element: <Navigate to="/training/manager?tab=matrix" replace />,
              },
            ],
          },

          // My Training
          {
            path: 'my-training',
            element: <MyTrainingListPage />,
          },
          {
            path: 'my-training/:assignmentId',
            element: <MyTrainingAssignmentPage />,
          },
          {
            // Instructor attestation viewer — pending external-evidence
            // attestations route here from the inbox + the program-overview
            // audit drawer.
            path: 'training/pending-signoffs/:progressId',
            element: <InstructorAttestationPage />,
          },
          {
            // Instructor sign-off viewer — pending live-observation
            // INSTRUCTOR_SIGNOFF items route here from the inbox.
            path: 'training/pending-instructor-signoffs/:progressId',
            element: <InstructorSignoffPage />,
          },
          // Records Manager (defaults to Records tab; ?tab=types for Types)
          {
            path: 'records',
            element: <RecordTypesListPage />,
          },
          // Legacy path — keep deep-links to the Types tab working
          {
            path: 'records/types',
            element: <Navigate to="/records?tab=types" replace />,
          },
          {
            path: 'records/types/new',
            element: <RecordTypeNewPage />,
          },
          {
            path: 'records/types/:id',
            element: <RecordTypeDetailPage />,
          },
          {
            path: 'records/types/:id/edit',
            element: <RecordTypeEditorPage />,
          },
          {
            path: 'records/new',
            element: <RecordNewPage />,
          },
          {
            path: 'records/:recordId',
            element: <RecordDetailPage />,
          },

          // Scheduling — Calendar (default view)
          {
            path: 'scheduling/calendar',
            element: <CalendarPage />,
          },

          // Scheduling — What's Due (cross-cutting operational view)
          {
            path: 'scheduling/whats-due',
            element: <WhatsDuePage />,
          },

          // Scheduling — rule management
          {
            path: 'scheduling/rules',
            element: <SchedulingPage />,
          },
          {
            path: 'scheduling/rules/new',
            element: <ScheduleRuleNewPage />,
          },
          {
            path: 'scheduling/rules/:id',
            element: <ScheduleRuleDetailPage />,
          },

          // Suppliers — Supplier Quality hub
          {
            path: 'suppliers',
            element: <SupplierListPage />,
          },
          {
            path: 'suppliers/record-types',
            element: <SupplierRecordTypesPage />,
          },
          {
            path: 'suppliers/:id',
            element: <SupplierDetailPage />,
          },

          // Other protected pages
          {
            path: 'notifications',
            element: <InboxPage />,
          },
          {
            path: 'profile',
            element: <ProfilePage />,
          },
          // Relay config — auth-gated only (not admin-gated). Any authenticated
          // user can view all sections; per-section edits are RBAC-gated in the
          // UI and enforced by the backend. Kept on the /admin/ path so existing
          // links + ?section= deep links keep working.
          {
            path: 'admin/relay-config',
            element: <AdminRelayConfigPage />,
          },
          // Lattice
          {
            path: 'lattice',
            element: <LatticeListPage />,
          },
          {
            path: 'lattice/new',
            element: <LatticeCreatePage />,
          },
          {
            path: 'lattice/:id',
            element: <LatticeDetailPage />,
          },

          // Procurement tracker — bound to the ERP `part` entity kind, so the
          // route carries no type id. Chromeless (see chromeless.ts).
          {
            path: 'procurement',
            element: <ProcurementPage />,
          },

          // Quotes — gated at runtime on the tenant's engine (QuotingRoute): the AES
          // Quote Desk for `aes-desk`, the generic workspace for `generic`, otherwise a
          // not-available state. Desk-only pages have no generic twin. Chromeless (see
          // chromeless.ts).
          {
            path: 'quotes',
            element: (
              <QuotingRoute
                aesDesk={quoteDesk(<QuoteManagementPage />)}
                generic={<GenericQuoteQueuePage />}
              />
            ),
          },
          // The quotes list used to live here; the home is `/` now and the list is `/quotes`.
          {
            path: 'quotes/manage',
            element: (
              <QuotingRoute aesDesk={<Navigate to="/quotes" replace />} />
            ),
          },
          {
            path: 'quotes/threads/:threadId',
            element: (
              <QuotingRoute aesDesk={quoteDesk(<QuoteIntakeRequestPage />)} />
            ),
          },
          {
            path: 'quotes/resources/pricing',
            element: (
              <QuotingRoute aesDesk={quoteDesk(<QuotePricingResourcePage />)} />
            ),
          },
          {
            path: 'quotes/:id',
            element: (
              <QuotingRoute
                aesDesk={quoteDesk(<QuoteWorkspacePage />)}
                generic={<GenericQuoteRequestPage />}
              />
            ),
          },

          // Scout — full-viewport AI workspace (Header is hidden by
          // ProtectedLayout's per-route hideHeader logic).
          {
            path: 'scout',
            element: <ScoutPage />,
          },

          {
            path: 'inbox',
            element: <TriagePage />,
          },
          {
            path: 'inbox/source',
            element: <InboxSourcePage />,
          },
          {
            path: 'triage',
            element: <LegacyInboxRedirect />,
          },
          {
            path: 'library',
            element: (
              <QuotingLibraryRedirect>
                <LibraryPage />
              </QuotingLibraryRedirect>
            ),
          },
          {
            path: 'home',
            element: (
              <QuotingHomeRedirect>
                <HomePage />
              </QuotingHomeRedirect>
            ),
          },
          // Read-only viewer for a shared Otto chat snapshot (chromeless).
          {
            path: 'chat/shared/:shareId',
            element: <SharedChatPage />,
          },

          // Admin routes (nested under AdminLayout for role-based authorization)
          {
            element: <AdminLayout />,
            children: [
              {
                path: 'admin/users',
                element: <AdminUsersPage />,
              },
              {
                path: 'admin/groups',
                element: <GroupsManagementPage />,
              },
              {
                path: 'admin/groups/:groupId',
                element: <GroupDetailPage />,
              },
              {
                path: 'admin/document-config',
                element: <Navigate to="/documents?tab=types" replace />,
              },
              {
                path: 'admin/master-data',
                element: <AdminMasterDataPage />,
              },
              {
                path: 'admin/master-data/object-types/:typeId',
                element: <ObjectTypeDetailPage />,
              },
              {
                path: 'admin/master-data/object-types/:typeId/records/:recordId',
                element: <ObjectRecordDetailPage />,
              },
            ],
          },

          // Super Admin routes (internal, requires Cognito + admin token)
          {
            path: 'super-admin/tenants',
            element: <SuperAdminTenantsPage />,
          },
        ],
      },

      // 404 catch-all route (must be last)
      {
        path: '*',
        element: (
          <Suspense fallback={<AppBootOverlay />}>
            <HideAppLoader />
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
];
